import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/throw';
import { SEARCH_DEFAULT_PAGE_SIZE } from './search-constants';
import { SearchCriteria } from './models/search-filter-model';
import { SavedSearchConfig } from './models/saved-search-config';
import { ResourceService } from 'sce-core';
import { CoreServiceRegistry } from '../../core/core-service-registry';
import { SearchService } from './search.service';

@Injectable()
export class SavedSearchService {

  private http$: HttpClient;
  private resourceService: ResourceService;

  constructor(private coreServiceRegistry: CoreServiceRegistry) {
    this.http$ = coreServiceRegistry.http$;
    this.resourceService = coreServiceRegistry.resourceService;
  }

  public getSavedSearchList(tableId: string) {
    return this.http$.get('api/data/loadsearch/' + tableId);
  }

  // save and update the search criteria
  public saveSearchCriteria(searchcriteriaName: any, searchId, type, searchService): any {
    const searchCondition = this.createRequestForCriteriaSave(searchcriteriaName, searchId, type, searchService);
    return this.sendSaveSearchCriteriaRequest(searchCondition);
  }

  private sendSaveSearchCriteriaRequest(request: any) {
    const response$ = this.http$.post(`api/data/search/save`, request)
      .map((response: any) => {
        return response;
      }).catch((error: any) => {
        if (error.error.errors.length !== 0) {
           if (error.error.errors[0].validationCode && error.error.errors[0].validationCode !== 200) {
            return Observable.throw(this.resourceService.get(error.error.errors['0'].validationMessage));
          } else if (error.status === 400) {
            return Observable.throw(error.error.errors[0].defaultMessage + ' - ' +
            error.error.errors['0'].rejectedValue);
           }
        }
      });
    return response$;
  }

  // method to create request object for save/upadte search
  private createRequestForCriteriaSave(searchName, searchId, type, searchService): any {
    const tableId = searchService.searchParams.tableId;
    const pageInfoReq = {
      tableId: tableId,
      pageNo: searchService.pagedData.pageDefinition.pageNumber,
      sortColumn: searchService.pagedData.pageDefinition.sortColumn,
      sortOrder: searchService.pagedData.pageDefinition.sortOrder,
      pageSize: SEARCH_DEFAULT_PAGE_SIZE,
      fullFetch: searchService.searchParams.isFullFetch
    };
    const payload = {
      pageInfo: pageInfoReq,
      searchCriteria: searchService.formatSearchCriteria(searchService.searchCriteria, searchService.searchParams.additionalParamMap)
    };
    const criteria = {
      'pageInfo': {
        'tableId': pageInfoReq.tableId,
        'pageNo': pageInfoReq.pageNo,
        'pageSize': pageInfoReq.pageSize,
        'fullFetch': pageInfoReq.fullFetch
      },
      'searchCriteria': payload.searchCriteria
    };
    const preferenceObject = {
      'searchId': searchId,
      'searchName': searchName,
      'type': type,
      'dataTableId': searchService.getMetadata() ? searchService.getMetadata().elementId : '',
      'criteria': JSON.stringify(criteria)
    };
    return (preferenceObject);
  }

  // method to load the search criteria from list.
  public loadSearchCriteria(criteriaId, savedSearchConfig) {
    return this.fetchSearchCriteria(criteriaId, savedSearchConfig);
  }

  // formatting the search criteria from server side format to  client side format
  public transformSearchCriteriaToModel(response, searchService) {
    let responseObject = null;
    try {
      responseObject = JSON.parse(response.criteria);
    } catch (Exception) {
      return null;
    }
    return this.createSearchCriteria(responseObject.searchCriteria.conditions, searchService);
   }
   /*This method will create search criteria based on conditions */
  public createSearchCriteria(conditions: any, searchService: SearchService): SearchCriteria {
    let responseObject = null;
    const conditionList = [];
    let additionalParamMap = searchService.searchParams.additionalParamMap;
    if (!additionalParamMap) {
      additionalParamMap = {};
    }
    conditions.forEach((condition, conditionIndex) => {
      const clauseReqList = [];
      condition.clauses.forEach((clause: any, clauseIndex: number) => {
        if (!additionalParamMap[clause.column] && clause.value || clause.value1) {
          clauseReqList.push({
            id: clauseIndex + 1,
            column: clause.column,
            operation: clause.operation,
            value: clause.value,
            value1: clause.value1,
            logicalOperation: clause.logicalOperation,
            typeOfField: clause.typeOfField
          });
        }
      });
      if (clauseReqList.length !== 0) {
        let logOp = null;
        if (conditionIndex < conditions.length - 1) {
          logOp = condition.logicalOperation;
        }
        conditionList.push({
          id: conditionIndex + 1,
          isMandatory: condition.mandatory,
          clauses: clauseReqList,
          operator: logOp,
          displayMode: 'VIEW',
          isReadOnly: false
        });
      }
    });
    if (conditionList.length > 0) {
      const lastCondition = conditionList.slice(conditionList.length - 1)[0];
      lastCondition.operator = conditionList[conditionList.length - 1].operator ? conditionList[conditionList.length - 1].operator : 'AND';
    }
    responseObject = new SearchCriteria();
    responseObject.conditions = conditionList;
    return responseObject;
  }

  // fetch the particular search
  public fetchSearchCriteria(searchId: any, savedSearchConfig: SavedSearchConfig) {
    const response$ = this.http$.get(`/api/data/criteria/` + searchId + `/fetch` )
      .map((response: any) => {
        // This savedSearchConfig is used as a reference in manage saved search component to display search Name
        savedSearchConfig.searchName = response['searchName'];
        savedSearchConfig.searchNameOrig = response['searchName'];
        savedSearchConfig.searchId = searchId;
        return response;
      }).catch((error: any) => {
        if (error.error.errors.length !== 0) {
          if (error.error.errors[0].validationCode !== 200) {
            return Observable.throw(this.resourceService.get(error.error.errors['0'].validationMessage));
          }
        }
      });
    return response$;
  }

  // method to delete search criteria
  public deleteSearchCriteria(searchIdsList: any) {
    let formData = new HttpParams();
    searchIdsList.forEach((searchId: any) => {
      formData = formData.append('ids', searchId);
    });
    const options = {
      headers: new HttpHeaders({
        'api' : 'scecommon',
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      body : formData.toString()
    };
    const response$ = this.http$.delete(`api/data/search/criteria`, options)
      .catch((error: any) => {
        if (error.error.errors.length !== 0) {
          if (error.error.errors[0].validationCode !== 200) {
            return Observable.throw(this.resourceService.get(error.error.errors['0'].validationMessage));
          }
        }
      });
    return response$;
  }
  // method to load all search criterias for the popup screen from side panel button
  public getAllSearchCriteriaList() {
    const response$ = this.http$.get('api/data/loadsearch/ALL')
      .catch((error: any) => {
        if (error.error.errors.length !== 0) {
          if (error.error.errors[0].validationCode !== 200) {
            return Observable.throw(this.resourceService.get(error.error.errors['0'].validationMessage));
          }
        }
      });
    return response$;
  }
}



// WEBPACK FOOTER //
// /home/runner/work/fbm-fulfillment-wms-client/fbm-fulfillment-wms-client/src/app/shared/search/saved-search.service.ts